import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  Filter,
  X,
  Star,
  Briefcase,
  ChevronDown,
  Grid,
  List,
  SortAsc,
  SortDesc,
} from "lucide-react";
import Navbar from "../components/common/Navbar";
import {
  Talent,
  SearchFilters,
  EXPERIENCE_OPTIONS,
  POSITION_OPTIONS,
  INTERNSHIP_OPTIONS,
  SORT_OPTIONS,
} from "../types/search";
import { mockTalents } from "../data/mockTalentData";

/**
 * SearchPage Component
 * Displays a searchable list of talent profiles with filtering and sorting capabilities
 * Features: Search, filters, sorting, pagination, and different view modes
 */
const SearchPage: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [talents, setTalents] = useState<Talent[]>([]);
  const [filteredTalents, setFilteredTalents] = useState<Talent[]>([]);
  const [filters, setFilters] = useState<SearchFilters>({
    query: "",
    location: "",
    experience: "",
    skills: [],
    position: "",
    hasInternship: "",
    availability: "",
    sortBy: "name",
    sortOrder: "asc",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [itemsPerPage] = useState(12);

  // Load talent data
  useEffect(() => {
    const loadTalents = async () => {
      setIsLoading(true);
      try {
        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setTalents(mockTalents);
        setFilteredTalents(mockTalents);
      } catch (error) {
        console.error("Failed to load talents:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTalents();
  }, []);

  // Filter and sort talents
  useEffect(() => {
    let filtered = [...talents];

    // Apply text search
    if (filters.query) {
      const query = filters.query.toLowerCase();
      filtered = filtered.filter(
        (talent) =>
          talent.name.toLowerCase().includes(query) ||
          talent.title.toLowerCase().includes(query) ||
          talent.skills.some((skill) => skill.toLowerCase().includes(query)) ||
          talent.education.toLowerCase().includes(query)
      );
    }

    // Apply location filter
    if (filters.location) {
      filtered = filtered.filter((talent) =>
        talent.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Apply experience filter
    if (filters.experience) {
      const [min, max] = filters.experience.split("-").map(Number);
      filtered = filtered.filter((talent) => {
        if (filters.experience === "10+") {
          return talent.experience >= 10;
        }
        if (max) {
          return talent.experience >= min && talent.experience <= max;
        }
        return talent.experience === min;
      });
    }

    // Apply position filter
    if (filters.position) {
      filtered = filtered.filter(
        (talent) => talent.position === filters.position
      );
    }

    // Apply internship filter
    if (filters.hasInternship) {
      const hasInternship = filters.hasInternship === "true";
      filtered = filtered.filter(
        (talent) => talent.hasInternship === hasInternship
      );
    }

    // Apply availability filter
    if (filters.availability) {
      const isAvailable = filters.availability === "available";
      filtered = filtered.filter(
        (talent) => talent.isAvailable === isAvailable
      );
    }

    // Apply skills filter
    if (filters.skills.length > 0) {
      filtered = filtered.filter((talent) =>
        filters.skills.some((skill) =>
          talent.skills.some((talentSkill) =>
            talentSkill.toLowerCase().includes(skill.toLowerCase())
          )
        )
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case "name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "experience":
          aValue = a.experience;
          bValue = b.experience;
          break;
        case "lastActive":
          aValue = new Date(a.lastActive);
          bValue = new Date(b.lastActive);
          break;
        case "projects":
          aValue = a.projects;
          bValue = b.projects;
          break;
        case "recommendations":
          aValue = a.recommendations;
          bValue = b.recommendations;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (filters.sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredTalents(filtered);
    setCurrentPage(1);
  }, [talents, filters]);

  // Handle filter changes
  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle skill addition
  const addSkill = (skill: string) => {
    if (skill && !filters.skills.includes(skill)) {
      setFilters((prev) => ({
        ...prev,
        skills: [...prev.skills, skill],
      }));
    }
  };

  // Handle skill removal
  const removeSkill = (skill: string) => {
    setFilters((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s !== skill),
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      query: "",
      location: "",
      experience: "",
      skills: [],
      position: "",
      hasInternship: "",
      availability: "",
      sortBy: "name",
      sortOrder: "asc",
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredTalents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTalents = filteredTalents.slice(startIndex, endIndex);

  // Talent Card Component
  const TalentCard: React.FC<{ talent: Talent }> = ({ talent }) => (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer relative"
      onClick={() => navigate(`/user/${talent.username}`)}
    >
      {/* 구직중 상태 - 우측 상단 */}
      {talent.isAvailable && (
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
            구직 중
          </span>
        </div>
      )}

      <div className="flex items-start gap-3 mb-3">
        <img
          src={talent.avatar}
          alt={talent.name}
          className="w-12 h-12 rounded-full object-cover"
        />
        <div className="flex-1 pr-16">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {talent.name}
          </h3>
          <p className="text-gray-600 text-sm mb-2">{talent.title}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <MapPin className="w-3 h-3" />
            <span>{talent.location}</span>
          </div>
          {talent.currentCompany && (
            <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
              {talent.companyLogo && (
                <img
                  src={talent.companyLogo}
                  alt={talent.currentCompany}
                  className="w-3 h-3 rounded"
                />
              )}
              <span>{talent.currentCompany}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1">
          <Briefcase className="w-3 h-3" />
          <span>{talent.experience}년 경력</span>
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3" />
          <span>{talent.projects}개 프로젝트</span>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex flex-wrap gap-1">
          {talent.skills.slice(0, 3).map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {skill}
            </span>
          ))}
          {talent.skills.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
              +{talent.skills.length - 3}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
          {talent.position}
        </span>
        {talent.experience === 0 && talent.hasInternship && (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
            인턴 경험
          </span>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">인재 정보를 불러오는 중...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">인재 검색</h1>
          <p className="text-gray-600">
            {filteredTalents.length}명의 인재를 찾았습니다
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          {/* Search Bar */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="이름, 직무, 기술, 학교로 검색..."
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span>필터</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  showFilters ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    지역
                  </label>
                  <input
                    type="text"
                    placeholder="지역 입력"
                    value={filters.location}
                    onChange={(e) =>
                      handleFilterChange("location", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Experience Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    경력
                  </label>
                  <select
                    value={filters.experience}
                    onChange={(e) =>
                      handleFilterChange("experience", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {EXPERIENCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Position Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    포지션
                  </label>
                  <select
                    value={filters.position}
                    onChange={(e) =>
                      handleFilterChange("position", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {POSITION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Internship Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    인턴 경험
                  </label>
                  <select
                    value={filters.hasInternship}
                    onChange={(e) =>
                      handleFilterChange("hasInternship", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {INTERNSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Skills Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  기술 스택
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {filters.skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {skill}
                      <button
                        onClick={() => removeSkill(skill)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="기술을 입력하고 Enter를 누르세요"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      addSkill(e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  필터 초기화
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg ${
                  viewMode === "grid"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg ${
                  viewMode === "list"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">정렬:</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() =>
                  handleFilterChange(
                    "sortOrder",
                    filters.sortOrder === "asc" ? "desc" : "asc"
                  )
                }
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                {filters.sortOrder === "asc" ? (
                  <SortAsc className="w-4 h-4" />
                ) : (
                  <SortDesc className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {currentTalents.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              검색 결과가 없습니다
            </h3>
            <p className="text-gray-600">다른 검색어나 필터를 시도해보세요</p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "space-y-4"
            }
          >
            {currentTalents.map((talent) => (
              <TalentCard key={talent.id} talent={talent} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-lg ${
                      currentPage === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
